import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleButton from "@/components/GoogleButton";
import { MapPin, Star } from "lucide-react";
import { photoSrc } from "@/components/RoomPhotosUploader";
import ShareButton from "@/components/ShareButton";
import FavoriteButton from "@/components/FavoriteButton";
import ReviewCard from "@/components/ReviewCard";
import { Hourglass } from "lucide-react";

export default function RoomDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [slots, setSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [modal, setModal] = useState(null); // slot to book, or {multi: [slots]}
  const [selected, setSelected] = useState([]);
  const emptyForm = () => ({ client_name: user?.role === "client" ? user.name : "", client_email: user?.role === "client" ? user.email : "", discipline: "", notes: "" });
  const [form, setForm] = useState(emptyForm);
  useEffect(() => { if (user && user.id) setForm((f) => (f.client_name || f.client_email ? f : emptyForm())); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleSelect = (sid) => setSelected((cur) => (cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid]));
  const selectSeries = (seriesId) => setSelected(slots.filter((s) => s.series_id === seriesId && s.status === "available" && new Date(s.start) > new Date()).map((s) => s.id));

  const load = () => {
    api.get(`/rooms/${id}`).then((r) => setRoom(r.data));
    api.get("/slots", { params: { room_id: id } }).then((r) => setSlots(r.data));
    api.get("/reviews", { params: { target_type: "room", target_id: id } }).then((r) => setReviews(r.data));
  };
  useEffect(load, [id]);

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("slot");
    if (!sid || !user || !user.id || !slots.length) return;
    const s = slots.find((x) => x.id === sid);
    if (s && s.status === "available") { setModal(s); toast.info("Votre créneau est prêt, finalisez votre demande."); }
    else toast.error("Ce créneau n'est plus disponible.");
    params.delete("slot"); setParams(params, { replace: true });
  }, [params, setParams, user, slots]);

  const guestBook = (s) => {
    localStorage.setItem("sc_google_room", id);
    localStorage.setItem("sc_google_slot", s.id);
    localStorage.removeItem("sc_google_role");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/tableau-de-bord";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const book = async () => {
    const payload = { ...form, client_email: form.client_email?.trim() || undefined };
    try {
      if (modal.multi) {
        const r = await api.post("/bookings/series", { slot_ids: modal.multi.map((s) => s.id), ...payload });
        toast.success(`${r.data.created} demandes envoyées à la salle en une fois.`);
        setSelected([]);
      } else {
        await api.post("/bookings", { slot_id: modal.id, ...payload });
        toast.success("Demande envoyée à la salle. Vous serez notifié à la validation.");
      }
      setModal(null);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const submitReview = async () => {
    try {
      const r = await api.post("/reviews", { target_type: "room", target_id: id, rating, comment });
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        await api.post(`/reviews/${r.data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => toast.error("Avis publié, mais la photo n'a pas pu être envoyée"));
      }
      toast.success("Merci pour votre avis !");
      setComment(""); setRating(5); setPhoto(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connectez-vous pour laisser un avis");
    }
  };

  if (!room) return <div><Navbar /><div className="max-w-5xl mx-auto p-6 text-slate-500">Chargement…</div></div>;
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "—";

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sc-fade-in">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {room.photo_url && <img src={photoSrc(room.photo_url)} alt={room.name} className="w-full h-72 object-cover rounded-2xl border border-slate-200" />}
            {(room.photos || []).length > 1 && (
              <div className="mt-2 grid grid-cols-4 gap-2" data-testid="room-gallery">
                {room.photos.filter((p) => p !== room.photo_url).slice(0, 8).map((p) => (
                  <img key={p} src={photoSrc(p)} alt="" className="h-20 w-full object-cover rounded-xl border border-slate-200" />
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900">{room.name}</h1>
              <div className="flex items-center gap-2">
                <FavoriteButton roomId={room.id} />
                <ShareButton testId="share-room" path={`/salles/${room.id}`} text={`Regarde cette salle sur TymCoach : ${room.name} (${room.city}) dès ${room.price_per_hour}€/h`} />
              </div>
            </div>
            <div className="mt-1 text-slate-600 flex items-center gap-1"><MapPin className="w-4 h-4" />{room.address} · {room.neighborhood} · {room.city}</div>
            <p className="mt-4 text-slate-700">{room.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {(room.equipments || []).map((e) => <span key={e} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{e}</span>)}
            </div>
          </div>
          <div className="sc-card">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Tarif</div>
            <div className="text-3xl font-extrabold text-slate-900 mt-1">{room.price_per_hour}€ <span className="text-base font-medium text-slate-500">/ heure</span></div>
            <div className="mt-4 text-xs uppercase font-semibold text-slate-500 tracking-wider">Coopération</div>
            <div className="mt-1 text-sm text-slate-800">
              {room.cooperation_level === "open" && "Ouverte — validation rapide."}
              {room.cooperation_level === "moderate" && "Modérée — validation manuelle."}
              {room.cooperation_level === "strict" && "Stricte — profil du coach étudié."}
            </div>
            <div className="mt-4 text-xs uppercase font-semibold text-slate-500 tracking-wider">Note moyenne</div>
            <div className="mt-1 flex items-center gap-2"><Star className="w-4 h-4 text-orange-500" /><span className="font-bold">{avg}</span><span className="text-xs text-slate-500">({reviews.length} avis)</span></div>
          </div>
        </div>

        <h2 className="mt-10 text-2xl font-bold text-slate-900">Créneaux disponibles</h2>
        {user === false && (
          <div data-testid="quick-login-banner" className="mt-4 sc-card flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-emerald-200 bg-emerald-50/40">
            <div>
              <div className="font-bold text-slate-900">Réservez en 10 secondes</div>
              <div className="text-sm text-slate-600">Connectez-vous pour demander un créneau — aucun abonnement, aucun engagement.</div>
            </div>
            <div className="sm:w-72"><GoogleButton roomId={id} /></div>
          </div>
        )}
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {slots.filter((s) => s.status === "available").map((s) => (
            <div key={s.id} data-testid={`slot-card-${s.id}`} className={`sc-card relative ${s.last_minute ? "border-amber-300 bg-amber-50/40" : ""} ${selected.includes(s.id) ? "ring-2 ring-emerald-500" : ""}`}>
              {user && user.id && ["coach", "client"].includes(user.role) && (
                <label className="absolute top-3 right-3 flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer">
                  <input data-testid={`select-slot-${s.id}`} type="checkbox" className="accent-emerald-500" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} /> multi
                </label>
              )}
              {s.last_minute && <div data-testid={`slot-last-minute-${s.id}`} className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">⚡ Dernière minute · coachs approuvés</div>}
              {s.series_id && !s.last_minute && <button data-testid={`select-series-${s.id}`} onClick={() => selectSeries(s.series_id)} className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 mb-1 hover:underline">↻ Récurrent · sélectionner toute la série</button>}
              <div className="text-sm text-slate-500">{new Date(s.start).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="text-lg font-bold text-slate-900">{new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} – {new Date(s.end).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="mt-1 text-sm text-emerald-700 font-semibold">{s.price || room.price_per_hour}€</div>
              <button
                data-testid={`book-slot-${s.id}`}
                disabled={!user || (user !== false && user.role === "room_manager")}
                onClick={() => (user === false ? guestBook(s) : setModal(s))}
                className="mt-3 sc-btn sc-btn-primary w-full justify-center disabled:opacity-50">
                {user && user !== false ? "Demander ce créneau" : "Se connecter avec Google pour réserver"}
              </button>
            </div>
          ))}
          {slots.filter((s) => s.status === "available").length === 0 && (
            <div className="text-slate-500 text-sm">Aucun créneau libre pour le moment.</div>
          )}
        </div>
        {selected.length > 0 && (
          <div data-testid="multi-select-bar" className="sticky bottom-4 mt-4 z-30 bg-slate-900 text-white rounded-2xl shadow-2xl p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm"><b>{selected.length}</b> créneau(x) sélectionné(s) · {selected.reduce((t, sid) => t + ((slots.find((s) => s.id === sid) || {}).price || room.price_per_hour), 0)} € au total</div>
            <div className="flex gap-2">
              <button data-testid="multi-clear" onClick={() => setSelected([])} className="sc-btn sc-btn-outline text-xs bg-transparent text-white border-white/30">Effacer</button>
              <button data-testid="multi-book" onClick={() => setModal({ multi: slots.filter((s) => selected.includes(s.id)) })} className="sc-btn sc-btn-emerald text-xs">Demander ces {selected.length} créneaux</button>
            </div>
          </div>
        )}

        {user && user.id && ["coach", "client"].includes(user.role) && slots.some((s) => s.status !== "available" && new Date(s.start) > new Date()) && (
          <>
            <h3 className="mt-8 text-lg font-bold text-slate-900 flex items-center gap-2"><Hourglass className="w-5 h-5 text-slate-500" /> Créneaux déjà réservés — liste d'attente</h3>
            <p className="text-sm text-slate-600 mt-1">Inscrivez-vous : nous vous prévenons immédiatement si le créneau se libère.</p>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {slots.filter((s) => s.status !== "available" && new Date(s.start) > new Date()).map((s) => {
                const inList = (s.waitlist || []).includes(user.id);
                return (
                  <div key={s.id} data-testid={`booked-slot-${s.id}`} className="sc-card bg-slate-50">
                    <div className="text-sm text-slate-500">{new Date(s.start).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                    <div className="text-lg font-bold text-slate-700">{new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} – {new Date(s.end).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <button data-testid={`waitlist-btn-${s.id}`} aria-pressed={inList}
                      onClick={async () => {
                        try {
                          if (inList) { await api.delete(`/slots/${s.id}/waitlist`); toast.success("Retiré de la liste d'attente"); }
                          else { const r = await api.post(`/slots/${s.id}/waitlist`); toast.success(`Inscrit en liste d'attente (position ${r.data.position})`); }
                          api.get("/slots", { params: { room_id: id } }).then((r) => setSlots(r.data));
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}
                      className={`mt-3 w-full justify-center sc-btn text-xs ${inList ? "sc-btn-emerald" : "sc-btn-outline"}`}>
                      <Hourglass className="w-3.5 h-3.5" /> {inList ? "En liste d'attente ✓" : "Me prévenir s'il se libère"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <h2 className="mt-12 text-2xl font-bold text-slate-900">Avis</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} canReply={!!(user && user.id && user.id === room.manager_id)} onReplied={() => api.get("/reviews", { params: { target_type: "room", target_id: id } }).then((x) => setReviews(x.data))} />
          ))}
          {reviews.length === 0 && <div className="text-slate-500 text-sm">Aucun avis pour le moment.</div>}
        </div>

        {user && user !== false && (
          <div className="mt-6 sc-card">
            <div className="font-semibold text-slate-900">Laisser un avis</div>
            <div className="mt-2 flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)} className={`w-8 h-8 rounded-full ${n <= rating ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>{n}</button>
              ))}
            </div>
            <textarea data-testid="review-comment" className="mt-3 w-full border border-slate-200 rounded-xl p-3 text-sm" rows={3} placeholder="Votre expérience…" value={comment} onChange={(e) => setComment(e.target.value)} />
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <span className="sc-btn sc-btn-outline text-xs">📷 Joindre une photo (optionnel)</span>
              <input data-testid="review-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
              {photo && <span data-testid="review-photo-name" className="truncate max-w-[200px]">{photo.name} <button type="button" onClick={() => setPhoto(null)} className="text-rose-600 ml-1">✕</button></span>}
            </label>
            <button data-testid="review-submit" onClick={submitReview} className="mt-3 sc-btn sc-btn-emerald">Publier l'avis</button>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 grid place-items-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-slate-900 text-lg">Demande de réservation</div>
            <div className="text-sm text-slate-500">{room.name} · {modal.multi ? `${modal.multi.length} créneaux sélectionnés` : new Date(modal.start).toLocaleString("fr-FR")}</div>
            {modal.multi && <div className="mt-1 text-xs text-slate-500 max-h-20 overflow-y-auto" data-testid="multi-slot-list">{modal.multi.map((s) => <div key={s.id}>• {new Date(s.start).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>)}</div>}
            <div className="mt-4 space-y-3">
              <input data-testid="booking-client-name" className="w-full border border-slate-200 rounded-xl p-3 text-sm" placeholder="Nom du client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <input data-testid="booking-client-email" className="w-full border border-slate-200 rounded-xl p-3 text-sm" placeholder="Email du client (optionnel)" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
              <input data-testid="booking-discipline" className="w-full border border-slate-200 rounded-xl p-3 text-sm" placeholder="Discipline (ex: Yoga)" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} />
              <textarea data-testid="booking-notes" className="w-full border border-slate-200 rounded-xl p-3 text-sm" rows={3} placeholder="Notes pour la salle" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="sc-btn sc-btn-outline">Annuler</button>
              <button data-testid="booking-submit" onClick={book} className="sc-btn sc-btn-emerald">Envoyer la demande</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
